import { useState } from "react";
import { InputText, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { AirlineGroup, AirlineOption } from "../../../types";
import { useGroupsQuery } from "../../../hooks/useGroupsQuery";
import { useAirlinesQuery } from "../../../hooks/useAirlinesQuery";
import { useCreateUserWithAccessMutation } from "../../../hooks/useCreateUserWithAccessMutation";
import { useAccessSelectorState } from "../../../hooks/useAccessSelectorState";
import { isValidEmail } from "../../../utils/email";
import { LoadingSpinner } from "../../LoadingSpinner";
import { AccessSelector } from "../AccessSelector";
import { UserManagementModal } from "../UserManagementModal";

type AddUserModalProps = {
  onClose: () => void;
};

export const AddUserModal = ({ onClose }: AddUserModalProps) => <AddUserModalLoader onClose={onClose} />;

const AddUserModalLoader = ({ onClose }: { onClose: () => void }) => {
  const i18n = useI18n();
  const { data: groups } = useGroupsQuery();
  const { data: airlines } = useAirlinesQuery();

  if (!groups || !airlines) {
    return (
      <UserManagementModal
        title={i18n.trans(t("flights_airline_portal_add_user_title"))}
        closeAriaLabel="Close add user modal"
        onClose={onClose}
        onSave={() => {}}
        saveDisabled
      >
        <LoadingSpinner mixin={{ minHeight: "40vh" }} />
      </UserManagementModal>
    );
  }

  return <AddUserModalContent groups={groups} airlines={airlines} onClose={onClose} />;
};

const AddUserModalContent = ({
  groups,
  airlines,
  onClose,
}: {
  groups: AirlineGroup[];
  airlines: AirlineOption[];
  onClose: () => void;
}) => {
  const i18n = useI18n();
  const { mutate, isPending, error } = useCreateUserWithAccessMutation();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const {
    mode,
    setMode,
    selectedGroupId,
    selectedAirlineIds,
    selectedSingleAirlineId,
    onGroupChange,
    onAirlineToggle,
    onSingleAirlineChange,
    isAccessValid,
  } = useAccessSelectorState();

  const handleSave = () => {
    if (!isValidEmail(email)) {
      setEmailError(true);
      return;
    }
    if (mode === "group") {
      mutate({ email, groupId: selectedGroupId!, airlineIds: selectedAirlineIds }, { onSuccess: onClose });
    } else {
      const airline = airlines.find((a) => a.id === selectedSingleAirlineId!);
      const groupForAirline = airline ? groups.find((g) => g.airlineCodes.includes(airline.code)) : null;
      mutate(
        { email, groupId: groupForAirline?.id ?? null, airlineIds: [selectedSingleAirlineId!] },
        { onSuccess: onClose }
      );
    }
  };

  return (
    <UserManagementModal
      title={i18n.trans(t("flights_airline_portal_add_user_title"))}
      closeAriaLabel="Close add user modal"
      onClose={onClose}
      onSave={handleSave}
      saveDisabled={!email || emailError || isPending || !isAccessValid}
      footerExtras={
        <>
          <Text variant="small_1">
            {mode === "group"
              ? i18n.trans(t("flights_airline_portal_add_edit_user_disclaimer_group"))
              : i18n.trans(t("flights_airline_portal_add_edit_user_disclaimer"))}
          </Text>
          {error && (
            <Text variant="small_1" color="destructive">
              {i18n.trans(t("flights_airline_portal_add_edit_general_error"))}
            </Text>
          )}
        </>
      }
    >
      <Stack gap={6}>
        <Stack gap={1}>
          <Text variant="emphasized_2">
            {i18n.trans(t("flights_airline_portal_user_management_list_email_header"))}
          </Text>
          <InputText
            name="email"
            size="large"
            placeholder={i18n.trans(t("flights_airline_portal_add_user_email_general_error"))}
            value={email}
            onChange={({ value }: { value: string }) => {
              setEmail(value);
              if (emailError) setEmailError(false);
            }}
            error={emailError ? i18n.trans(t("flights_airline_portal_add_user_email_general_error")) : undefined}
          />
        </Stack>

        <AccessSelector
          groups={groups}
          airlines={airlines}
          mode={mode}
          onModeChange={setMode}
          selectedGroupId={selectedGroupId}
          selectedAirlineIds={selectedAirlineIds}
          selectedSingleAirlineId={selectedSingleAirlineId}
          onGroupChange={onGroupChange}
          onAirlineToggle={onAirlineToggle}
          onSingleAirlineChange={onSingleAirlineChange}
        />
      </Stack>
    </UserManagementModal>
  );
};
